import React from 'react';
import { User, MapPin, Phone, Calendar, Book, Mail, GraduationCap, Briefcase, Copy, Check } from 'lucide-react';

interface AboutProps {
  language: string;
}

interface PersonalInfoItem {
  label: string;
  value: string;
  icon: React.ReactNode;
  url?: string;
  isSocial?: boolean;
  socialIcon?: React.ReactNode;
}

const About: React.FC<AboutProps> = ({ language }) => {
  const [copiedEmail, setCopiedEmail] = React.useState(false);

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const translations = {
    en: {
      title: 'About Me',
      personalInfo: 'Personal Info',
      aboutMe: 'About Me',
      aboutMeText: "Hi there,\nI'm interested in software development and technology.",
      personalInfoItems: [
        { label: 'Name', value: 'Nguyen Ngoc Thanh Tai', icon: <User size={18} /> },
        { label: 'University', value: 'Hue University of Sciences', icon: <GraduationCap size={18} /> },
        { label: 'Address', value: 'Hue City, Vietnam', icon: <MapPin size={18} /> },
        { label: 'Field of Study', value: 'Information Technology', icon: <Book size={18} /> },
        { label: 'Phone', value: '(+84) 366 063 944', icon: <Phone size={18} /> },
        { label: 'Gmail', value: 'ntainguyen.it@gmail.com', icon: <Mail size={18} /> },
      ] as PersonalInfoItem[]
    },
    vi: {
      title: 'GIỚI THIỆU',
      personalInfo: 'Thông Tin Cá Nhân',
      aboutMe: 'Giới Thiệu',
      aboutMeText: 'Chào bạn,\nMình có sự quan tâm và yêu thích đối với phát triển phần mềm và công nghệ.',
      personalInfoItems: [
        { label: 'Tên', value: 'Nguyễn Ngọc Thành Tài', icon: <User size={18} /> },
        { label: 'Đại Học', value: 'Đại học Khoa học Huế', icon: <GraduationCap size={18} /> },
        { label: 'Địa Chỉ', value: 'Thành phố Huế, Việt Nam', icon: <MapPin size={18} /> },
        { label: 'Ngành Học', value: 'Công Nghệ Thông Tin', icon: <Book size={18} /> },
        { label: 'Điện Thoại', value: '(+84) 366 063 944', icon: <Phone size={18} /> },
        { label: 'Gmail', value: 'ntainguyen.it@gmail.com', icon: <Mail size={18} /> },
      ] as PersonalInfoItem[]
    }
  };

  const t = translations[language as keyof typeof translations];

  return (
    <section id="about" className="pt-10 pb-20 px-6 rounded-xl">
      <div className="max-w-5xl mx-auto">
        
        
        <div className="text-center mb-16" data-aos="fade-up">
            <h2 className={`text-5xl md:text-6xl text-center mb-4 uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-200 dark:to-gray-400 py-4 font-black leading-normal`}>
              {t.title}
            </h2>
        </div>
        
        {/* Personal Info Tab */}
        <div className="transition-all duration-500 opacity-100">
          <div className="bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-2xl shadow-sm p-8 border border-slate-200/60 dark:border-zinc-800" data-aos="fade-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.personalInfoItems.map((info, index) => (
                <div 
                  key={index} 
                  className="relative flex items-center bg-blue-50/80 dark:bg-blue-900/20 backdrop-blur-sm rounded-2xl border border-slate-200/60 dark:border-zinc-800/50 p-4 shadow-sm transition-all duration-300 bounce-pop-effect hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:hover:shadow-[0_0_30px_rgba(96,165,250,0.5)] active:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:active:shadow-[0_0_30px_rgba(96,165,250,0.5)]"
                  data-aos="fade-up"
                  data-aos-delay={index * 50}
                >
                  {info.label === 'Gmail' && (
                     <button
                       onClick={() => handleCopyEmail(info.value)}
                       className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors group z-10"
                       title={language === 'vi' ? 'Sao chép email' : 'Copy email'}
                     >
                        {copiedEmail ? (
                           <div className="flex items-center gap-1 text-green-500">
                             <Check size={14} />
                             <span className="text-[10px] font-medium">{language === 'vi' ? 'Đã sao chép!' : 'Copied!'}</span>
                           </div>
                        ) : (
                           <Copy size={14} className="group-hover:scale-110 transition-transform" />
                        )}
                     </button>
                  )}
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full text-blue-600 dark:text-blue-400 mr-4 shadow-md">
                    {info.icon}
                  </div>
                  <div className="flex flex-col flex-grow">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">{info.label}</span>
                    {info.isSocial ? (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-800 dark:text-blue-300 font-medium">{info.value}</span>
                        <a 
                          href={info.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:shadow-lg transition-all duration-300 hover:scale-110 flex items-center justify-center"
                          aria-label={info.label}
                        >
                          {info.socialIcon}
                        </a>
                      </div>
                    ) : (
                      'url' in info ? (
                        <a 
                          href={info.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <span className="text-gray-800 dark:text-blue-300 font-medium">{info.value}</span>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-6 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 dark:border-zinc-800 shadow-sm" data-aos="fade-up" data-aos-delay="100">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 pb-2 border-b-2 border-blue-200 dark:border-blue-800 inline-block">{t.aboutMe}</h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-left whitespace-pre-line">
                {t.aboutMeText}
              </p>
            </div>
          </div>
        </div>

        {/* Qualifications Section */}
        <div className="mt-12 bg-white/90 dark:bg-black/90 backdrop-blur-sm rounded-2xl shadow-sm p-8 border border-slate-200/60 dark:border-zinc-800" data-aos="fade-up">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Education Column */}
            <div>
              <div className="flex items-center mb-6">
                <GraduationCap size={24} className="text-blue-600 dark:text-blue-400 mr-3" />
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white pb-2 border-b-2 border-blue-200 dark:border-blue-800">
                  {language === 'en' ? 'Education' : 'Học Vấn'}
                </h3>
              </div>
              
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-900"></div>
                
                <div className="space-y-6">
                  {(language === 'en' ? [
                    {
                      title: 'Bachelor of Information Technology',
                      institution: 'Hue University of Sciences',
                      period: '2023 - 2027',
                      description: ''
                    },
                    {
                      title: 'High School Diploma',
                      institution: 'Tran Van Ky High School',
                      period: '2020 - 2023',
                      description: ''
                    }
                  ] : [
                    {
                      title: 'Cử Nhân Công Nghệ Thông Tin',
                      institution: 'Đại Học Khoa Học Huế',
                      period: '2023 - 2027',
                      description: ''
                    },
                    {
                      title: 'Tốt Nghiệp THPT',
                      institution: 'Trường THPT Trần Văn Kỷ',
                      period: '2020 - 2023',
                      description: ''
                    }
                  ]).map((item, index) => (
                    <div 
                      key={index} 
                      className="relative pl-10"
                      data-aos="fade-right"
                      data-aos-delay={index * 100}
                    >
                      <div className="absolute left-0 top-3 w-6 h-6 flex items-center justify-center">
                        <div className="w-3 h-3 bg-blue-600 dark:bg-blue-500 rounded-full shadow-md"></div>
                      </div>
                      
                      <div className="bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm p-5 rounded-2xl border border-slate-200/60 dark:border-zinc-800 shadow-sm transition-all duration-300 bounce-pop-effect hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:hover:shadow-[0_0_30px_rgba(96,165,250,0.5)] active:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:active:shadow-[0_0_30px_rgba(96,165,250,0.5)]">
                        <h4 className="text-lg font-medium text-gray-800 dark:text-white">{item.title}</h4>
                        <p className="text-gray-600 dark:text-gray-300">{item.institution}</p>
                        <div className="flex items-center mt-1 mb-2">
                          <Calendar size={14} className="text-blue-600 dark:text-blue-400 mr-1" />
                          <span className="text-sm text-blue-600 dark:text-blue-400">{item.period}</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Experience Column */}
            <div>
              <div className="flex items-center mb-6">
                <Briefcase size={24} className="text-blue-600 dark:text-blue-400 mr-3" />
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white pb-2 border-b-2 border-blue-200 dark:border-blue-800">
                  {language === 'en' ? 'Experience' : 'Kinh Nghiệm'}
                </h3>
              </div>
              
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-900"></div>
                
                <div className="space-y-6">
                  {(language === 'en' ? [
                    {
                      title: 'Intern...',
                      company: 'Company',
                      period: '2026 - Present',
                      description: 'Description'
                    }
                  ] : [
                    {
                      title: 'Thực Tập Sinh...',
                      company: 'Công ty',
                      period: '2026 - Hiện tại',
                      description: 'Mô tả'
                    }
                  ]).map((item, index) => (
                    <div 
                      key={index} 
                      className="relative pl-10"
                      data-aos="fade-left"
                      data-aos-delay={index * 100}
                    >
                      <div className="absolute left-0 top-3 w-6 h-6 flex items-center justify-center">
                        <div className="w-3 h-3 bg-blue-600 dark:bg-blue-500 rounded-full shadow-md"></div>
                      </div>
                      
                      <div className="bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm p-5 rounded-2xl border border-slate-200/60 dark:border-zinc-800 shadow-sm transition-all duration-300 bounce-pop-effect hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:hover:shadow-[0_0_30px_rgba(96,165,250,0.5)] active:shadow-[0_0_30px_rgba(37,99,235,0.5)] dark:active:shadow-[0_0_30px_rgba(96,165,250,0.5)]">
                        <h4 className="text-lg font-medium text-gray-800 dark:text-white">{item.title}</h4>
                        <p className="text-gray-600 dark:text-gray-300">{item.company}</p>
                        <div className="flex items-center mt-1 mb-2">
                          <Calendar size={14} className="text-blue-600 dark:text-blue-400 mr-1" />
                          <span className="text-sm text-blue-600 dark:text-blue-400">{item.period}</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(About);
